import React from 'react';
import { Zap, Layout, FileText, CheckCircle } from 'lucide-react';
import { ScanForm } from './ScanForm';

interface LandingPageProps {

    onScanSubmit: (data: any) => void;
    error?: string;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onScanSubmit, error }) => {
    return (
        <div className="relative">
            {/* Background Decor */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-purple-200 rounded-full blur-[120px] opacity-30"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-200 rounded-full blur-[120px] opacity-30"></div>
            </div>

            {/*
            - **Hero Section**: Mesh gradient background and large typography.
            - **Scan Results Page**:
              - **Glass Dashboard**: High-level metrics in floating glass cards.
              - **Animated Scores**: SVG-based score visualization.
              - **Clean Typography**: Improved readability for issue lists.
            */}
            <div className="relative pt-10 pb-20 sm:pt-16 sm:pb-24 overflow-hidden">
                <div className="text-center max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary-50 border border-primary-100 text-primary-600 text-sm font-medium mb-6 animate-fade-in-up">
                        <span className="flex h-2 w-2 rounded-full bg-primary-600 mr-2"></span>
                        v2.0 Now Available
                    </div>
                    <h1 className="text-5xl sm:text-7xl font-extrabold tracking-tight text-gray-900 mb-8 leading-tight">
                        Web Accessibility <br />
                        <span className="text-gradient-primary">Made Simple</span>
                    </h1>
                    <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto mb-10">
                        Automated WCAG 2.2 & Section 508 compliance testing for modern engineering teams.
                        Fast, accurate, and actionable reports.
                    </p>

                    {/* Scan Form Container */}
                    <div className="max-w-3xl mx-auto relative z-10">
                        <div className="glass-card p-4 sm:p-8 transform transition-all hover:scale-[1.01] duration-500 ring-1 ring-gray-900/5">
                            <ScanForm onSubmit={onScanSubmit} isLoading={false} error={error} />
                        </div>

                    </div>
                </div>
            </div>

            {/* Bento Grid Features */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold text-gray-900">Everything you need for compliance</h2>
                    <p className="mt-4 text-gray-600">Comprehensive tools for developers and QA teams.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Feature 1: Large Card (Spans 2 cols) */}
                    <div className="md:col-span-2 glass p-8 rounded-2xl relative overflow-hidden group transition-all hover:shadow-xl">
                        <div className="relative z-10">
                            <div className="h-12 w-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-6">
                                <Zap className="h-6 w-6 text-indigo-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Lightning Fast Analysis</h3>
                            <p className="text-gray-600 text-lg max-w-md">Powered by high-performance engines to deliver comprehensive audits in seconds, not minutes. Real-time DOM analysis.</p>
                        </div>
                        <div className="absolute right-0 bottom-0 w-64 h-64 bg-gradient-to-tl from-indigo-50 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </div>

                    {/* Feature 2: Tall Card (Spans 2 rows) */}
                    <div className="glass p-8 rounded-2xl md:row-span-2 flex flex-col transition-all hover:shadow-xl">
                        <div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center mb-6 w-fit">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">WCAG Compliance</h3>
                        <p className="text-gray-600 mb-8">Full checks against WCAG 2.1/2.2 AA & AAA standards.</p>
                        <div className="mt-auto space-y-4">
                            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                                <span className="text-sm font-medium text-gray-700">Screen Readers</span>
                            </div>
                            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                                <span className="text-sm font-medium text-gray-700">Color Contrast</span>
                            </div>
                            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                                <span className="text-sm font-medium text-gray-700">Keyboard Nav</span>
                            </div>
                            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                                <span className="text-sm font-medium text-gray-700">ARIA Attributes</span>
                            </div>
                        </div>
                    </div>

                    {/* Feature 3: Standard Card */}
                    <div className="glass p-8 rounded-2xl transition-all hover:shadow-xl">
                        <div className="h-12 w-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                            <FileText className="h-6 w-6 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Exportable Reports</h3>
                        <p className="text-gray-600">Download professional PDF & JSON reports instantly.</p>
                    </div>

                    {/* Feature 4: Standard Card */}
                    <div className="glass p-8 rounded-2xl transition-all hover:shadow-xl">
                        <div className="h-12 w-12 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                            <Layout className="h-6 w-6 text-orange-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Visual Dashboard</h3>
                        <p className="text-gray-600">Track scores and issues with intuitive charts.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};
